import { keyframes } from "@emotion/css";
import styled from "@emotion/styled";
import React, { FC } from "react";
import translate from "src/lib/translate";
import { TranslationKey } from "src/types/translationKey";
import ClockGIF from "src/ui/assets/images/clock.gif";
import Translate from "src/ui/components/Translate/Translate";

const pulseScale = keyframes`
  0%   { transform: scale(1); opacity: 1; }
  12%  { transform: scale(1.35); opacity: 0; }
  12.1% { transform: scale(1); opacity: 0; }
  12.2% { transform: scale(1); opacity: 0.6; }
  24%  { transform: scale(1.35); opacity: 0; }
  24.1% { transform: scale(1); opacity: 0; }
  24.2% { transform: scale(1); opacity: 0.6; }
  36%  { transform: scale(1.35); opacity: 0; }
  36.1% { transform: scale(1); opacity: 0; }
  36.2% { transform: scale(1); opacity: 0.6; }
  48%  { transform: scale(1.35); opacity: 0; }
  48.1% { transform: scale(1); opacity: 0; }
  48.2% { transform: scale(1); opacity: 0.6; }

`;

//#region Styled components
const StepWrapper = styled.div`
  position: relative;
  display: flex;
  gap: 0.75rem;

  /* Add gray connecting line to end of step status icon */
  &::before {
    content: "";
    position: absolute;
    top: 2rem;
    left: calc(1rem - 0.5px);
    width: 1px;
    height: calc(100% - 2rem);
    //background: var(--color-gray-lighter, #e6e3db);
  }

  /* Add sunrise connecting line to end of step status icon for steps that are not followed by 'LOCKED' step */
  &:not(:has(+ [data-status="LOCKED"])) {
    &::before {
      background: var(
        --Sunrise-Vertical-Regular,
        linear-gradient(1deg, #80aeff 13.49%, #f7bde6 51.22%, #ffbd70 85.11%)
      );
    }
  }

  &:has(+ [data-status="LOCKED"]) {
    &::before {
      background: var(--color-white, #ffffff);
    }
  }

  /* Hide connecting line on last step */
  &:last-of-type {
    &::before {
      display: none;
    }

    &[data-show-last-line="true"]::before {
      display: block;
    }

    &[data-show-last-line="true"][data-status="LOCKED"]::before,
    &[data-show-last-line="true"][data-status="PENDING"]::before {
      background: var(--light-grey-gradient);
    }
  }

  &:not([data-status="LOCKED"]):not([data-status="PENDING"]) {
    /* Add sunrise background and border to steps that are not 'LOCKED' */
    > div:first-of-type {
      background: var(
        --Sunrise-Vertical-Regular,
        linear-gradient(181deg, #80aeff 13.49%, #f7bde6 51.22%, #ffbd70 85.11%)
      );

      &::before {
        background: var(
          --Sunrise-Vertical-Lightest,
          linear-gradient(
            181deg,
            #c1d7fb 13.49%,
            #fddee9 50.42%,
            #ffdfb4 85.11%
          )
        );
      }
    }
  }

  &[data-status="IN_PROGRESS"] {
    /* Add circle icon to 'IN_PROGRESS' steps */
    > div:first-of-type {
      &::after {
        content: url('data:image/svg+xml;utf8, <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8.8" cy="8.8" r="8.8" fill="url(%23paint0_linear_29904_13545)"/><defs><linearGradient id="paint0_linear_29904_13545" x1="8.8" y1="17.6" x2="8.8" y2="2.62261e-07" gradientUnits="userSpaceOnUse"><stop stop-color="%230E3763"/><stop offset="0.526042" stop-color="%235B4855"/><stop offset="1" stop-color="%238A6947"/></linearGradient></defs></svg>');
      }
    }

    /* Pulse ring outside the circle */
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 2rem;
      height: 2rem;
      border-radius: 50%;
      background: var(--color-gray, #bfc6d4);
      animation: ${pulseScale} 5s ease-in-out infinite;
      z-index: -1;
    }

    p,
    small {
      color: var(--color-charcoal, #212121);
    }
  }

  &[data-status="PENDING"] {
    /* Add clock gif icon to 'PENDING' steps */
    > div:first-of-type {
      &::after {
        content: "";
        background: url(${ClockGIF});
        background-position: center;
        background-size: cover;
        background-repeat: no-repeat;
        width: 1.5rem;
        height: 1.5rem;
      }
    }

    p,
    small {
      color: var(--color-charcoal, #212121);
    }
  }

  &[data-status="COMPLETED"] {
    /* Add checkmark icon to 'COMPLETED' steps */
    > div:first-of-type {
      &::after {
        content: url('data:image/svg+xml;utf8, <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none"><path fill="url(%23a)" stroke="url(%23b)" stroke-linejoin="round" d="M6.965 13.4 3.133 9.57l.569-.55 3.263 3.263 6.93-6.948.587.568-7.517 7.499Z"/><defs><linearGradient id="a" x1="3.133" x2="14.482" y1="9.367" y2="9.367" gradientUnits="userSpaceOnUse"><stop stopColor="%230E3763"/><stop offset=".526" stopColor="%235B4855"/><stop offset="1" stopColor="%238A6947"/></linearGradient><linearGradient id="b" x1="3.133" x2="14.482" y1="9.367" y2="9.367" gradientUnits="userSpaceOnUse"><stop stopColor="%230E3763"/><stop offset=".526" stopColor="%235B4855"/><stop offset="1" stopColor="%238A6947"/></linearGradient></defs></svg>');
      }
    }

    p,
    small {
      color: var(--color-charcoal-60, rgba(33, 33, 33, 0.6));
      text-decoration: line-through;
    }
  }

  &[data-status="SKIPPED"] {
    /* Add x icon to 'SKIPPED' steps */
    > div:first-of-type {
      &::after {
        display: block;
        margin-left: 0.175rem;
        margin-top: -0.125rem;
        content: url('data:image/svg+xml,<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.59215 8.88579L5.36587 5.65951L4.74361 5.03725L1.33252 1.62616L1.95477 1.00391L5.36587 4.415L5.98812 5.03725L9.2144 8.26354L8.59215 8.88579Z" fill="url(%23paint0_linear_22262_26610)"/><path d="M5.36587 4.415L5.98812 5.03725L9.32551 1.69986L8.70325 1.07761L5.36587 4.415Z" fill="url(%23paint1_linear_22262_26610)"/><path d="M5.36587 5.65951L4.74361 5.03725L1.44362 8.33724L2.06588 8.95949L5.36587 5.65951Z" fill="url(%23paint2_linear_22262_26610)"/><path d="M5.36587 4.415L5.98812 5.03725M5.36587 4.415L8.70325 1.07761L9.32551 1.69986L5.98812 5.03725M5.36587 4.415L1.95477 1.00391L1.33252 1.62616L4.74361 5.03725M5.36587 4.415L4.74361 5.03725M5.98812 5.03725L9.2144 8.26354L8.59215 8.88579L5.36587 5.65951M5.98812 5.03725L5.36587 5.65951M5.36587 5.65951L4.74361 5.03725M5.36587 5.65951L2.06588 8.95949L1.44362 8.33724L4.74361 5.03725" stroke="url(%23paint3_linear_22262_26610)" stroke-width="1.2" stroke-linejoin="round"/><defs><linearGradient id="paint0_linear_22262_26610" x1="1.33252" y1="4.9817" x2="9.32551" y2="4.9817" gradientUnits="userSpaceOnUse"><stop stopColor="%230E3763"/><stop offset="0.526042" stopColor="%235B4855"/><stop offset="1" stopColor="%238A6947"/></linearGradient><linearGradient id="paint1_linear_22262_26610" x1="1.33252" y1="4.9817" x2="9.32551" y2="4.9817" gradientUnits="userSpaceOnUse"><stop stopColor="%230E3763"/><stop offset="0.526042" stopColor="%235B4855"/><stop offset="1" stopColor="%238A6947"/></linearGradient><linearGradient id="paint2_linear_22262_26610" x1="1.33252" y1="4.9817" x2="9.32551" y2="4.9817" gradientUnits="userSpaceOnUse"><stop stopColor="%230E3763"/><stop offset="0.526042" stopColor="%235B4855"/><stop offset="1" stopColor="%238A6947"/></linearGradient><linearGradient id="paint3_linear_22262_26610" x1="1.33252" y1="4.9817" x2="9.32551" y2="4.9817" gradientUnits="userSpaceOnUse"><stop stopColor="%230E3763"/><stop offset="0.526042" stopColor="%235B4855"/><stop offset="1" stopColor="%238A6947"/></linearGradient></defs></svg>');
      }
    }

    p,
    small {
      color: var(--color-charcoal-60, rgba(33, 33, 33, 0.6));
      text-decoration: line-through;
    }
  }
`;

const StepStatusIcon = styled.div`
  position: relative;
  min-width: 2rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: var(--color-gray-lighter, #e6e3db);

  &::before {
    content: "";
    position: absolute;
    inset: 1px;
    border-radius: 50%;
    background: #fff;
  }

  &::after {
    content: "";
    width: 1.1rem;
    height: 1.1rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const StepTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0.25rem 0 1.25rem;
`;

const StepLabel = styled.span`
  font-weight: 500;
  font-size: 0.75rem;
  line-height: 140%;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 5px 0 0;
  display: inline-block;
`;

const StepTitlePillWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const StepTitle = styled.p`
  font-weight: 500;
  color: var(--color-charcoal, #212121);
  margin: 0;
`;

const StyledPill = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: max-content;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.24);
  backdrop-filter: blur(8px);
  padding: 5px 7px;

  small {
    font-weight: 600;
    font-size: 10px;
    background: var(--gradient-dusk);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

//#endregion

export enum StepStatus {
  COMPLETED = "COMPLETED",
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  LOCKED = "LOCKED",
  SKIPPED = "SKIPPED"
}

export type Step = {
  status: StepStatus;
  title: TranslationKey;
  subtitle?: TranslationKey;
  showStatus?: boolean;
  label?: TranslationKey;
  context?: string;
  showLastLine?: boolean;
};

export interface Variables {
  weekday?: string;
  startTime?: string;
  endTime?: string;
  timeZone?: string;
}

type StepListProps = {
  steps: Step[];
  variables?: Variables;
};

const StepList: FC<StepListProps> = ({ steps, variables }) => {
  return (
    <div>
      {steps.map((step) => (
        <StepWrapper
          key={step.title}
          data-status={step.status}
          data-show-last-line={step.showLastLine}
        >
          <StepStatusIcon />
          <StepTextWrapper>
            {step.label && <StepLabel>{translate(step.label)}</StepLabel>}
            <StepTitlePillWrapper>
              <StepTitle>
                {translate(step.title, { context: step.context })}
              </StepTitle>
              {step.showStatus && (
                <StyledPill>
                  <small style={{ textTransform: "uppercase" }}>
                    <Translate
                      msg="onboarding.status"
                      variables={{ context: step.status.toLowerCase() }}
                    />
                  </small>
                </StyledPill>
              )}
            </StepTitlePillWrapper>

            {step.subtitle && (
              <small>
                <Translate
                  msg={step.subtitle}
                  variables={{ context: step.context, ...variables }}
                  noWrap
                />
              </small>
            )}
          </StepTextWrapper>
        </StepWrapper>
      ))}
    </div>
  );
};

export default StepList;
